import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import { sendAIMessage } from '../services/ai-assistant-service';
import type { AIMessage, AIAssistantState } from '../types/ai-assistant-types';

const WELCOME_MESSAGE: AIMessage = {
  id: 'welcome',
  role: 'assistant',
  content:
    "Hello! I'm your KaiMed AI assistant. I can help you navigate the app, understand your test results, manage appointments, get medication information, and answer health questions. How can I help you today?",
  timestamp: new Date().toISOString(),
  suggestions: [
    'Explain my test results',
    'Help me book an appointment',
    'What are my current medications?',
    'Summarize my health records',
  ],
};

export const useAIAssistantStore = create<AIAssistantState>()(
  persist(
    (set, get) => ({
      messages: [WELCOME_MESSAGE],
      isLoading: false,
      error: null,
      isWidgetOpen: false,
      isWidgetMinimized: false,
      isFullPage: false,
      currentPage: '/',
      currentPageTitle: 'Home',

      sendMessage: async (content: string) => {
        const userMessage: AIMessage = {
          id: uuidv4(),
          role: 'user',
          content,
          timestamp: new Date().toISOString(),
        };

        set((state) => ({
          messages: [...state.messages, userMessage],
          isLoading: true,
          error: null,
        }));

        try {
          const { messages, currentPage, currentPageTitle } = get();
          const history = messages
            .filter((m) => m.role !== 'system' && m.id !== 'welcome')
            .map((m) => ({
              role: m.role as 'user' | 'assistant',
              content: m.content,
            }));

          const response = await sendAIMessage({
            message: content,
            conversation_history: history,
            context: {
              current_page: currentPage,
              current_page_title: currentPageTitle,
            },
          });

          const assistantMessage: AIMessage = {
            id: uuidv4(),
            role: 'assistant',
            content: response.message,
            timestamp: new Date().toISOString(),
            actions: response.actions,
            suggestions: response.suggestions,
            metadata: response.metadata
              ? {
                  model: response.metadata.model,
                  tokensUsed: response.metadata.tokens_used,
                }
              : undefined,
          };

          set((state) => ({
            messages: [...state.messages, assistantMessage],
            isLoading: false,
          }));
        } catch (error: any) {
          const errorMessage: AIMessage = {
            id: uuidv4(),
            role: 'assistant',
            content:
              'I apologize, but I encountered an error processing your request. Please try again.',
            timestamp: new Date().toISOString(),
            error: error?.message || 'Unknown error',
          };

          set((state) => ({
            messages: [...state.messages, errorMessage],
            isLoading: false,
            error: error?.message || 'Failed to send message',
          }));
        }
      },

      clearConversation: () =>
        set({
          messages: [{ ...WELCOME_MESSAGE, timestamp: new Date().toISOString() }],
          error: null,
        }),

      toggleWidget: () =>
        set((state) => ({
          isWidgetOpen: !state.isWidgetOpen,
          isWidgetMinimized: false,
        })),

      minimizeWidget: () => set({ isWidgetMinimized: true }),
      expandWidget: () => set({ isWidgetMinimized: false }),
      setFullPage: (isFullPage: boolean) => set({ isFullPage }),

      setPageContext: (page: string, title: string) =>
        set({ currentPage: page, currentPageTitle: title }),

      retryLastMessage: async () => {
        const { messages, sendMessage } = get();
        const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
        if (lastUserMsg) {
          const lastUserIndex = messages.lastIndexOf(lastUserMsg);
          set({ messages: messages.slice(0, lastUserIndex), error: null });
          await sendMessage(lastUserMsg.content);
        }
      },
    }),
    {
      name: 'mediconnect-ai-assistant',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        messages: state.messages,
        isWidgetOpen: state.isWidgetOpen,
      }),
    },
  ),
);
